'use client'

import { useState } from 'react'
import Link from '@/lib/link'
import { BookOpen, MessagesSquare, Bug, LifeBuoy, ArrowUpRight, Smartphone } from 'lucide-react'
import { BugReportModal } from '@/components/bug-report-modal'

const docLinks = [
  {
    title: 'Documentation',
    description: 'Desktop and mobile guides for installation, radios, cables, and editing.',
    icon: BookOpen,
    href: '/docs',
  },
  {
    title: 'Beginners Guide',
    description: 'Never programmed a radio before? Follow the desktop or Android walkthrough.',
    icon: LifeBuoy,
    href: '/beginners-guide',
  },
  {
    title: 'NewChirp Mobile',
    description: 'Real app screenshots for Bluetooth, USB OTG, channel editing, and updates.',
    icon: Smartphone,
    href: '/docs#mobile',
  },
  {
    title: 'Community',
    description: 'Ask questions and get help from the community and developers.',
    icon: MessagesSquare,
    href: '/community',
  },
]

const bugCard = {
  title: 'Report a Bug',
  description: 'Found an issue or need a new radio supported? File a report.',
  icon: Bug,
}

export function DocsCommunity() {
  const [bugOpen, setBugOpen] = useState(false)
  const BugIcon = bugCard.icon

  return (
    <section id="docs" className="border-b border-border/60 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4">
        <div id="community" className="max-w-2xl scroll-mt-20">
          <span className="font-mono text-sm text-primary">// docs &amp; community</span>
          <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight sm:text-4xl">
            Learn it, and get help when you need it
          </h2>
          <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">
            NewChirp builds on more than a decade of CHIRP documentation and a friendly community of
            operators. Everything below is right here on NewChirp.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          {docLinks.map((link) => {
            const Icon = link.icon
            return (
              <Link
                key={link.title}
                href={link.href}
                className="group flex items-start gap-4 rounded-xl border border-border bg-card p-6 transition-colors hover:border-primary/40 hover:bg-secondary/60"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <h3 className="flex items-center gap-1 text-lg font-semibold">
                    {link.title}
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {link.description}
                  </p>
                </div>
              </Link>
            )
          })}

          <button
            type="button"
            onClick={() => setBugOpen(true)}
            className="group flex items-start gap-4 rounded-xl border border-border bg-card p-6 text-left transition-colors hover:border-primary/40 hover:bg-secondary/60"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary">
              <BugIcon className="h-5 w-5" />
            </span>
            <div className="flex-1">
              <h3 className="flex items-center gap-1 text-lg font-semibold">
                {bugCard.title}
                <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {bugCard.description}
              </p>
            </div>
          </button>
        </div>
      </div>

      <BugReportModal open={bugOpen} onClose={() => setBugOpen(false)} />
    </section>
  )
}
